import { useState, useEffect, useMemo } from 'react';
import API from '@/lib/api';
import { toast } from 'sonner';
import { Palette, Ruler, MagicWand, Plus, X, Eye } from '@phosphor-icons/react';

/**
 * VariantCodeSettings — Instellingen-panel voor automatische variant-artikelcode generatie.
 *
 * Toont:
 *  - Format-template ({base}{kleur}{maat} enz.)
 *  - Live preview op basis van voorbeeld-combo
 *  - Default kleur-afkortingen (read-only tabel, zoekbaar)
 *  - Eigen overrides: kleur ➝ afkorting (bv. "Limoen" → "LMN")
 *  - Eigen overrides: maat ➝ afkorting (bv. "38.5" → "385")
 */
export default function VariantCodeSettings() {
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [format, setFormat] = useState('{base}{kleur}{maat}');
  const [kleurMap, setKleurMap] = useState({}); // {Zwart: 'ZWA'}
  const [maatMap, setMaatMap] = useState({});   // {'38.5': '385'}
  const [defaultKleur, setDefaultKleur] = useState([]);
  const [filter, setFilter] = useState('');

  // Voorraad-afboek per document-type (toggleable)
  const [afboekTypes, setAfboekTypes] = useState(['factuur', 'leverbon', 'bestelbon']);
  // Variant-info zichtbaar op PDF per document-type
  const [variantOpPdfTypes, setVariantOpPdfTypes] = useState(['leverbon', 'bestelbon', 'creditnota', 'proforma']);

  // Preview
  const [previewBase, setPreviewBase] = useState('26SW001');
  const [previewKleur, setPreviewKleur] = useState('Black');
  const [previewMaat, setPreviewMaat] = useState('36');
  const [previewCode, setPreviewCode] = useState('');

  // Input-velden voor toevoegen van eigen overrides
  const [newKleurNaam, setNewKleurNaam] = useState('');
  const [newKleurAfk, setNewKleurAfk] = useState('');
  const [newMaatNaam, setNewMaatNaam] = useState('');
  const [newMaatAfk, setNewMaatAfk] = useState('');

  useEffect(() => {
    Promise.all([
      API.get('/artikels/variant-code/defaults').catch(() => ({ data: {} })),
      API.get('/instellingen').catch(() => ({ data: {} })),
    ]).then(([codeResp, instResp]) => {
      const d = codeResp.data || {};
      setFormat(d.tenant_format || '{base}{kleur}{maat}');
      setKleurMap(d.tenant_kleur_afkortingen || {});
      setMaatMap(d.tenant_maat_afkortingen || {});
      setDefaultKleur(d.default_kleur_afkortingen || []);
      const inst = instResp.data || {};
      if (Array.isArray(inst.voorraad_afboek_types)) setAfboekTypes(inst.voorraad_afboek_types);
      if (Array.isArray(inst.variant_op_pdf_types)) setVariantOpPdfTypes(inst.variant_op_pdf_types);
      setLoaded(true);
    });
  }, []);

  // Live preview — debounced
  useEffect(() => {
    if (!loaded) return;
    const t = setTimeout(() => {
      API.post('/artikels/variant-code/preview', {
        base_code: previewBase,
        combo: { kleur: previewKleur, maat: previewMaat },
        cfg: { format, kleur_afkortingen: kleurMap, maat_afkortingen: maatMap },
      }).then(r => setPreviewCode(r.data?.code || '')).catch(() => setPreviewCode(''));
    }, 300);
    return () => clearTimeout(t);
  }, [loaded, format, kleurMap, maatMap, previewBase, previewKleur, previewMaat]);

  const save = async () => {
    setSaving(true);
    try {
      await API.put('/instellingen', {
        art_variant_code: {
          format: format || '{base}{kleur}{maat}',
          kleur_afkortingen: kleurMap,
          maat_afkortingen: maatMap,
        },
        voorraad_afboek_types: afboekTypes,
        variant_op_pdf_types: variantOpPdfTypes,
      });
      toast.success('✅ Variant-code instellingen opgeslagen');
    } catch (e) {
      toast.error('Opslaan mislukt', { description: e?.response?.data?.detail || e.message });
    } finally {
      setSaving(false);
    }
  };

  const toggleInArray = (value, arr, setter) => {
    setter(arr.includes(value) ? arr.filter(x => x !== value) : [...arr, value]);
  };

  const addKleur = () => {
    const n = (newKleurNaam || '').trim();
    const a = (newKleurAfk || '').trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (!n || !a) { toast.error('Vul zowel kleurnaam als afkorting in'); return; }
    setKleurMap(m => ({ ...m, [n]: a.slice(0, 6) }));
    setNewKleurNaam(''); setNewKleurAfk('');
  };
  const rmKleur = (k) => {
    setKleurMap(m => { const n = { ...m }; delete n[k]; return n; });
  };

  const addMaat = () => {
    const n = (newMaatNaam || '').trim();
    const a = (newMaatAfk || '').trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (!n || !a) { toast.error('Vul zowel maatwaarde als afkorting in'); return; }
    setMaatMap(m => ({ ...m, [n]: a.slice(0, 6) }));
    setNewMaatNaam(''); setNewMaatAfk('');
  };
  const rmMaat = (k) => {
    setMaatMap(m => { const n = { ...m }; delete n[k]; return n; });
  };

  const filteredDefault = useMemo(() => {
    const q = (filter || '').trim().toLowerCase();
    if (!q) return defaultKleur;
    return defaultKleur.filter(r => r.kleur.toLowerCase().includes(q) || r.afkorting.toLowerCase().includes(q));
  }, [defaultKleur, filter]);

  const FORMAT_PRESETS = [
    { label: 'Compact (aanbevolen)', tpl: '{base}{kleur}{maat}', voorbeeld: '26SW001BLK36' },
    { label: 'Met streepjes', tpl: '{base}-{kleur}-{maat}', voorbeeld: '26SW001-BLK-36' },
    { label: 'Maat voor kleur', tpl: '{base}{maat}{kleur}', voorbeeld: '26SW00136BLK' },
    { label: 'Kleur eerst (dash)', tpl: '{base}-{kleur}-{maat}', voorbeeld: '26SW001-BLK-36' },
  ];

  return (
    <div className="section-card" data-testid="var-code-settings-card">
      <div className="section-header">
        <span className="section-title inline-flex items-center gap-2">
          <MagicWand size={16} weight="duotone" />
          Automatische variant-artikelcode
        </span>
        <button
          type="button"
          className={`btn btn-sm btn-primary`}
          onClick={save}
          disabled={saving || !loaded}
          data-testid="var-code-save-btn"
        >
          {saving ? 'Opslaan...' : 'Opslaan'}
        </button>
      </div>

      <div className="p-5 space-y-6">
        {/* Uitleg */}
        <div className="text-sm text-gray-600 leading-relaxed">
          Wanneer je varianten genereert op een artikel (bv. basis-code <b>26SW001</b> met kleur <b>Black</b> en maat <b>36</b>),
          bouwt het systeem automatisch een unieke variant-artikelcode zoals <span className="font-mono text-[#002FA7]">26SW001BLK36</span>.
          Kleurnamen worden automatisch afgekort (NL én EN herkend). Pas hier het formaat en de afkortingen aan naar jouw voorkeur.
        </div>

        {/* Format-template + presets */}
        <div>
          <label className="form-label block mb-1.5">Format-template</label>
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            {FORMAT_PRESETS.map((p, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setFormat(p.tpl)}
                className={`btn btn-sm ${format === p.tpl ? 'btn-primary' : 'btn-outline'}`}
                data-testid={`var-code-preset-${i}`}
                title={p.voorbeeld}
              >
                {p.label}
              </button>
            ))}
          </div>
          <input
            type="text"
            className="field-input font-mono text-sm"
            value={format}
            onChange={e => setFormat(e.target.value)}
            placeholder="{base}{kleur}{maat}"
            data-testid="var-code-format-input"
          />
          <div className="text-[11px] text-gray-500 mt-1">
            Placeholders: <code>{'{base}'}</code> (artikel-code), <code>{'{kleur}'}</code>, <code>{'{maat}'}</code>,
            <code>{'{dim1}'}</code>/<code>{'{dim2}'}</code>/<code>{'{dim3}'}</code> (op volgorde van dimensies).
          </div>
        </div>

        {/* Live preview */}
        <div className="bg-[#F8FAFC] border border-gray-200 p-4 rounded">
          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-1.5">
            <Eye size={14} /> Live voorbeeld
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
            <div>
              <label className="form-label text-[10px]">Basis-code</label>
              <input className="field-input" value={previewBase} onChange={e => setPreviewBase(e.target.value)} data-testid="var-code-preview-base" />
            </div>
            <div>
              <label className="form-label text-[10px]">Kleur</label>
              <input className="field-input" value={previewKleur} onChange={e => setPreviewKleur(e.target.value)} data-testid="var-code-preview-kleur" />
            </div>
            <div>
              <label className="form-label text-[10px]">Maat</label>
              <input className="field-input" value={previewMaat} onChange={e => setPreviewMaat(e.target.value)} data-testid="var-code-preview-maat" />
            </div>
            <div>
              <div className="form-label text-[10px]">Gegenereerde code</div>
              <div className="font-mono text-lg font-bold text-[#002FA7] tracking-wider" data-testid="var-code-preview-result">
                {previewCode || '—'}
              </div>
            </div>
          </div>
        </div>

        {/* Kleur-afkortingen: user-overrides */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Palette size={16} className="text-[#F97316]" weight="duotone" />
            <div className="text-sm font-semibold text-gray-800">Eigen kleur-afkortingen (overrides)</div>
            <div className="text-[11px] text-gray-500">— hebben voorrang op de standaard-lijst</div>
          </div>
          <div className="flex items-center gap-2 mb-3">
            <input
              className="field-input"
              placeholder="Kleurnaam (bv. Limoen)"
              value={newKleurNaam}
              onChange={e => setNewKleurNaam(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && addKleur()}
              style={{ maxWidth: 200 }}
              data-testid="var-code-new-kleur-naam"
            />
            <input
              className="field-input font-mono"
              placeholder="Afk. (bv. LMN)"
              value={newKleurAfk}
              onChange={e => setNewKleurAfk(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && addKleur()}
              style={{ maxWidth: 120 }}
              maxLength={6}
              data-testid="var-code-new-kleur-afk"
            />
            <button type="button" className="btn btn-sm btn-outline" onClick={addKleur} data-testid="var-code-add-kleur">
              <Plus size={14} /> Toevoegen
            </button>
          </div>
          {Object.keys(kleurMap).length === 0 ? (
            <div className="text-xs text-gray-400 italic">Nog geen eigen afkortingen. De standaard-lijst hieronder wordt gebruikt.</div>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {Object.entries(kleurMap).map(([k, v]) => (
                <span key={k} className="inline-flex items-center gap-1.5 px-2 py-1 bg-orange-50 border border-orange-200 text-xs font-mono">
                  <span className="text-gray-700">{k}</span>
                  <span className="text-gray-400">→</span>
                  <span className="text-[#D97706] font-bold">{v}</span>
                  <button onClick={() => rmKleur(k)} className="ml-1 text-red-400 hover:text-red-600" data-testid={`var-code-rm-kleur-${k}`}>
                    <X size={11} />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Maat-afkortingen: user-overrides */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Ruler size={16} className="text-[#002FA7]" weight="duotone" />
            <div className="text-sm font-semibold text-gray-800">Eigen maat-afkortingen (overrides)</div>
            <div className="text-[11px] text-gray-500">— bv. "38.5" → "385" (als jij halve maten wél gebruikt), of skip halve maten door niets toe te voegen</div>
          </div>
          <div className="flex items-center gap-2 mb-3">
            <input
              className="field-input"
              placeholder='Maatwaarde (bv. "38.5" of "Extra Large")'
              value={newMaatNaam}
              onChange={e => setNewMaatNaam(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && addMaat()}
              style={{ maxWidth: 260 }}
              data-testid="var-code-new-maat-naam"
            />
            <input
              className="field-input font-mono"
              placeholder="Afk. (bv. 385 of XL)"
              value={newMaatAfk}
              onChange={e => setNewMaatAfk(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && addMaat()}
              style={{ maxWidth: 140 }}
              maxLength={6}
              data-testid="var-code-new-maat-afk"
            />
            <button type="button" className="btn btn-sm btn-outline" onClick={addMaat} data-testid="var-code-add-maat">
              <Plus size={14} /> Toevoegen
            </button>
          </div>
          {Object.keys(maatMap).length === 0 ? (
            <div className="text-xs text-gray-400 italic">Nog geen overrides. Standaard wordt de maat-waarde zelf gebruikt (alleen letters/cijfers, bv. "38.5" → "385").</div>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {Object.entries(maatMap).map(([k, v]) => (
                <span key={k} className="inline-flex items-center gap-1.5 px-2 py-1 bg-blue-50 border border-blue-200 text-xs font-mono">
                  <span className="text-gray-700">{k}</span>
                  <span className="text-gray-400">→</span>
                  <span className="text-[#002FA7] font-bold">{v}</span>
                  <button onClick={() => rmMaat(k)} className="ml-1 text-red-400 hover:text-red-600" data-testid={`var-code-rm-maat-${k}`}>
                    <X size={11} />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Standaard-lijst (read-only, zoekbaar) */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <div className="text-sm font-semibold text-gray-800">Standaard kleur-afkortingen</div>
            <div className="text-[11px] text-gray-500">— herkent NL én EN namen ({defaultKleur.length} ingangen)</div>
            <input
              type="text"
              className="field-input ml-auto text-xs"
              placeholder="Filter..."
              value={filter}
              onChange={e => setFilter(e.target.value)}
              style={{ maxWidth: 160 }}
              data-testid="var-code-kleur-filter"
            />
          </div>
          <div className="border border-gray-200 bg-white max-h-[280px] overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="text-left px-3 py-2 font-semibold text-gray-600 w-1/2">Naam</th>
                  <th className="text-left px-3 py-2 font-semibold text-gray-600">Afkorting</th>
                </tr>
              </thead>
              <tbody>
                {filteredDefault.map((r, i) => (
                  <tr key={i} className="border-t border-gray-100 hover:bg-gray-50">
                    <td className="px-3 py-1.5">{r.kleur}</td>
                    <td className="px-3 py-1.5 font-mono font-bold text-gray-700">{r.afkorting}</td>
                  </tr>
                ))}
                {filteredDefault.length === 0 && (
                  <tr><td colSpan={2} className="px-3 py-4 text-center text-gray-400 italic">Geen resultaten voor "{filter}"</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Variant-voorraad: auto-afboek per document-type */}
        <div className="border-t border-gray-200 pt-5">
          <div className="flex items-center gap-2 mb-2">
            <div className="text-sm font-semibold text-gray-800">📦 Variant-voorraad automatisch afboeken bij:</div>
          </div>
          <div className="text-xs text-gray-500 mb-2 leading-relaxed">
            Vink de documenttypes aan waarbij de voorraad van de gekozen variant automatisch moet worden verminderd wanneer het document geactiveerd wordt (factuur openstaand, leverbon geleverd, enz.). Dit geldt zowel voor master-voorraad als variant-voorraad.
          </div>
          <div className="flex flex-wrap gap-2">
            {[
              { key: 'factuur', label: 'Factuur' },
              { key: 'leverbon', label: 'Leverbon' },
              { key: 'bestelbon', label: 'Bestelbon' },
            ].map(t => (
              <label key={t.key} className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 bg-white cursor-pointer hover:bg-gray-50 text-sm" data-testid={`var-code-afboek-${t.key}`}>
                <input
                  type="checkbox"
                  checked={afboekTypes.includes(t.key)}
                  onChange={() => toggleInArray(t.key, afboekTypes, setAfboekTypes)}
                />
                <span>{t.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Variant-info tonen op PDF per document-type */}
        <div className="border-t border-gray-200 pt-5">
          <div className="flex items-center gap-2 mb-2">
            <div className="text-sm font-semibold text-gray-800">📄 Variant-info tonen op PDF van:</div>
          </div>
          <div className="text-xs text-gray-500 mb-2 leading-relaxed">
            Vink aan op welke documenttypes het variant-label "· Zwart / 36" moet verschijnen in de omschrijving op de PDF.
            De variant-code (bv. <span className="font-mono">27SW001BLK36</span>) verschijnt ALTIJD in de REF-kolom — deze instelling betreft enkel de leesbare "· Kleur / Maat" suffix.
          </div>
          <div className="flex flex-wrap gap-2">
            {[
              { key: 'factuur', label: 'Factuur' },
              { key: 'offerte', label: 'Offerte' },
              { key: 'leverbon', label: 'Leverbon' },
              { key: 'bestelbon', label: 'Bestelbon' },
              { key: 'creditnota', label: 'Creditnota' },
              { key: 'proforma', label: 'Proforma' },
            ].map(t => (
              <label key={t.key} className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 bg-white cursor-pointer hover:bg-gray-50 text-sm" data-testid={`var-code-pdf-${t.key}`}>
                <input
                  type="checkbox"
                  checked={variantOpPdfTypes.includes(t.key)}
                  onChange={() => toggleInArray(t.key, variantOpPdfTypes, setVariantOpPdfTypes)}
                />
                <span>{t.label}</span>
              </label>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
